import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { X } from "lucide-react";
import { Medication } from "../OrderInfo/OrderDetails/MedicationRow";
import defaultAvatar from "../../../../../assets/images/default_avatar.jpg";
import { AdminOrderService } from "../../../../../services/api/AdminOrderService/admin.order.service";
import dandarLogo from "../../../../../assets/images/dandar-logo.png";
import html2canvas from "html2canvas";

interface PrescriptionCardProps {
  formData: {
    userDetails: {
      name: string;
      phone: string;
      avatar?: string;
      gender?: string;
      age?: string;
    };
    chiefComplaints: string[];
    diagnosis: string[];
    medications: Medication[];
    vitals?: {
      weight?: string;
      temperature?: string;
      pulseRate?: string;
      heartRate?: string;
      spo2?: string;
      glucose?: string;
      respiratoryRate?: string;
      vegetarian?: boolean;
      bloodPressureMin?: string;
      bloodPressureMax?: string;
    };
    specialInstructions?: string[];
    doctorDetails?: {
      name: string;
      education: string;
      specialization: string;
      registrationNumber: string;
      hospitalName: string;
      address: string;
      state: string;
      city: string;
      pincode: string;
      mobileNumber: string;
      signatureImage?: string;
    };
    appointmentDate?: string;
    startTime?: string;
    followUpDate?: string;
  };
  patientId?: string;
}

interface PatientDetails {
  name: string;
  mobile_number: string;
  profile_image?: string;
  gender?: string;
  age?: string;
}

export type PrescriptionCardHandle = {
  capturePrescriptionImage: () => Promise<string | null>;
};

const PrescriptionCard = forwardRef<
  PrescriptionCardHandle,
  PrescriptionCardProps
>(({ formData, patientId }, ref) => {
  console.log("formdataaa", formData);
  const [showPreview, setShowPreview] = useState(false);
  const [patientDetails, setPatientDetails] = useState<PatientDetails | null>(
    null
  );
  const previewRef = useRef<HTMLDivElement>(null);

  const capturePrescriptionImage = async (): Promise<string | null> => {
    setShowPreview(true);

    // Wait for the preview to be rendered
    await new Promise((resolve) => setTimeout(resolve, 500)); // Increased timeout

    if (!previewRef.current) {
      console.error("Preview ref not available");
      return null;
    }

    try {
      // Store original styles
      const originalStyles = {
        position: previewRef.current.style.position,
        overflow: previewRef.current.style.overflow,
        height: previewRef.current.style.height,
        width: previewRef.current.style.width,
      };

      // Make the container full size for capture
      previewRef.current.style.position = "absolute";
      previewRef.current.style.overflow = "visible";
      previewRef.current.style.height = "auto";
      previewRef.current.style.width = "auto";

      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        logging: true,
        backgroundColor: "#FFFFFF",
        scrollX: 0,
        scrollY: 0,
        windowWidth: previewRef.current.scrollWidth,
        windowHeight: previewRef.current.scrollHeight,
        onclone: (clonedDoc, element) => {
          // Ensure all content is visible in the clone
          const clonedPreview = element as HTMLElement;
          clonedPreview.style.position = "absolute";
          clonedPreview.style.overflow = "visible";
          clonedPreview.style.height = "auto";
          clonedPreview.style.width = "auto";
        },
      });

      const dataUrl = canvas.toDataURL("image/png");
      return dataUrl;
    } catch (error) {
      console.error("Error capturing prescription:", error);
      return null;
    } finally {
      setShowPreview(false);
    }
  };

  useImperativeHandle(ref, () => ({
    capturePrescriptionImage,
  }));

  const handleImageClick = () => {
    setShowPreview(true);
  };

  const handleClose = () => {
    setShowPreview(false);
  };

  useEffect(() => {
    if (!patientId) return;

    const getPatientDetails = async () => {
      try {
        const response = await AdminOrderService.getAdminPatientDetailsById(
          patientId
        );
        setPatientDetails(response.data);
      } catch (error) {
        console.log(error);
      }
    };

    getPatientDetails();
  }, [patientId]);

  const formatToArray = (text: string | string[]) => {
    if (Array.isArray(text)) return text;
    if (!text) return [];
    return text.split("\n").filter((item) => item.trim() !== "");
  };

  return (
    <div className="mb-6">
      {/* Thumbnail Card (unchanged) */}
      <div
        className="relative w-auto bg-white rounded-lg border border-gray-200 p-4 cursor-pointer shadow-sm"
        onClick={handleImageClick}
      >
        <div className="flex items-center gap-4 mb-4">
          <img
            src={patientDetails?.profile_image || defaultAvatar}
            alt="profile"
            className="w-12 h-12 rounded-full"
          />
          <div>
            <h3 className="font-bold text-md">
              {formData?.userDetails?.name || patientDetails?.name}
            </h3>
            <p className="text-gray-500 text-sm">
              {formData?.userDetails?.phone || patientDetails?.mobile_number}
            </p>
          </div>
        </div>

        <div className="mb-3">
          <h4 className="text-sm font-medium text-gray-700 mb-1">
            Chief Complaints:
          </h4>
          {formData?.chiefComplaints?.length > 0 ? (
            <ul className="text-sm text-gray-600 list-disc pl-5">
              {formData.chiefComplaints.map((complaint, index) => (
                <li key={index}>{complaint}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">No complaints recorded</p>
          )}
        </div>

        <div className="mb-3">
          <h4 className="text-sm font-medium text-gray-700 mb-1">Diagnosis:</h4>
          {formData?.diagnosis?.length > 0 ? (
            <ul className="text-sm text-gray-600 list-disc pl-5">
              {formData.diagnosis.map((diag, index) => (
                <li key={index}>{diag}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">No diagnosis recorded</p>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">
            Medications:
          </h4>
          {formData?.medications?.length > 0 ? (
            <ul className="text-sm text-gray-600 list-disc pl-5">
              {formData.medications.slice(0, 2).map((med, index) => (
                <li key={index}>
                  {med.name} {med.salt} - {med.dosage}x/day for {med.duration} days
                </li>
              ))}
              {formData.medications.length > 2 && (
                <li>+{formData.medications.length - 2} more...</li>
              )}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">No medications prescribed</p>
          )}
        </div>
      </div>

      {/* Fullscreen Preview - Updated to match section JSX */}
      {showPreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto"
          style={{
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
          }}
        >
          <div
            className="relative bg-white rounded-lg w-[850px] max-h-[90vh] shadow-xl p-6 overflow-y-auto"
            ref={previewRef}
            style={{
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
            }}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              aria-label="Close Preview"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col items-center">
              <div className="flex gap-10 pt-5 pb-3 w-full">
                <img
                  src={dandarLogo}
                  alt="dandar-logo"
                  className="h-16 w-25 mb-2"
                />
                {formData.doctorDetails && (
                  <div className="w-full flex justify-between mt-2">
                    <div>
                      <span className="text-base font-medium">
                        DR. {formData.doctorDetails.name}
                      </span>
                      <div className="pt-1">
                        <p className="font-medium">
                          {formData.doctorDetails.education}
                        </p>
                        <p>{formData.doctorDetails.specialization}</p>
                        <p>
                          Reg.No. {formData.doctorDetails.registrationNumber}
                        </p>
                      </div>
                    </div>
                    <div
                      className="flex flex-col text-xs gap-2 font-medium"
                      style={{ color: "#929292" }}
                    >
                      <div>
                        <p>
                          {formData.doctorDetails.hospitalName},{" "}
                          {formData.doctorDetails.address},{" "}
                          {formData.doctorDetails.state},{" "}
                          {formData.doctorDetails.city}
                        </p>
                        <p>{formData.doctorDetails.pincode}</p>
                      </div>
                      <div
                        className="flex text-xs gap-2"
                        style={{ color: "#929292" }}
                      >
                        <span>{formData.doctorDetails.mobileNumber}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <hr className="mt-2 mb-6 w-full" />

              <div
                className="flex justify-between w-full"
                style={{ color: "#8f4570", fontWeight: "500" }}
              >
                <div>
                  <div className="text-gray-800 pb-4">
                    <div className="pb-2 text-[#8f4570] text-[1.1em] font-semibold">
                      Patient
                    </div>
                    <div className="font-semibold">
                      {formData?.userDetails?.name ||
                        patientDetails?.name ||
                        ""}
                      ,
                      <span className="pl-1">
                        {formData?.userDetails?.gender ||
                          patientDetails?.gender ||
                          ""}{" "}
                        {formData?.userDetails?.age ||
                          patientDetails?.age ||
                          ""}
                      </span>
                    </div>
                    <div className="font-semibold">
                      Mobile:{" "}
                      <span className="font-normal text-gray-600">
                        {formData?.userDetails?.phone ||
                          patientDetails?.mobile_number ||
                          ""}
                      </span>
                    </div>
                  </div>
                  <div className="pb-2 mt-2 flex flex-col gap-2">
                    <div className="text-[#8f4570] text-[1.1em] font-semibold">
                      Chief Complaints:
                    </div>
                    <div className="font-medium text-gray-700">
                      <ul className="list-none space-y-2">
                        {formData?.chiefComplaints?.length > 0 ? (
                          formData.chiefComplaints.map((complaint, index) => (
                            <li key={index} className="flex items-start">
                              <span
                                style={{ color: "#29a359", marginRight: "8px" }}
                              >
                                •
                              </span>
                              <span>{complaint}</span>
                            </li>
                          ))
                        ) : (
                          <li className="flex items-start">
                            <span
                              style={{ color: "#29a359", marginRight: "8px" }}
                            >
                              •
                            </span>
                            <span>N/A</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                <div
                  className="mr-4"
                  style={{ color: "black", fontWeight: "500" }}
                ></div>
              </div>

              {/* Diagnosis Section */}
              <div className="pb-4 pt-4 text-[1.1em] font-semibold text-[#8f4570] w-full">
                Diagnosis/ Provisional Diagnosis
              </div>
              <div className="w-full">
                <table className="table-auto border-collapse w-full text-left">
                  <thead>
                    <tr className="border-t-2 border-b-2 border-dashed border-gray-400">
                      <th className="w-3/10 px-4 py-2">Diagnosis Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData?.diagnosis?.length > 0 ? (
                      formData.diagnosis.map((diag, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="w-3/10 px-4 py-2">{diag}</td>
                          <td className="w-7/10 px-4 py-2">-</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={2}
                          className="px-4 py-2 text-gray-500 text-sm"
                        >
                          No diagnosis recorded
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Medications Section */}
              <div className="pb-4 pt-4 text-[1.1em] font-semibold text-[#8f4570] w-full">
                Medication Prescribed
              </div>
              <div className="w-full">
                <table className="table-auto border-collapse w-full text-left">
                  <thead>
                    <tr className="border-t-2 border-b-2 border-dashed border-gray-400">
                      <th className="px-4 py-2 text-left">Medicine Name</th>
                      <th className="px-4 py-2 text-left">Medicine Salt</th>
                      <th className="px-4 py-2 text-left">Dosage</th>
                      <th className="px-4 py-2 text-left">Medicine Details</th>
                      <th className="px-4 py-2 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData?.medications?.length > 0 ? (
                      formData.medications.map((med, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-left font-medium">
                            {med.name}
                          </td>
                          <td className="px-4 py-2 text-left font-medium">
                            {med.salt}
                          </td>
                          <td className="px-4 py-2 text-left">
                            {med.dosage} per day
                          </td>
                          <td className="px-4 py-2 text-left font-medium">
                            {med.details}
                          </td>
                          <td className="px-4 py-2 text-left">
                            {med.duration} days
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-2 text-gray-500 text-sm text-center"
                        >
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="flex text-sm mt-4 justify-between">
                  <div className="border border-solid border-black p-1 rounded-lg">
                    <span style={{ color: "black", fontWeight: "700" }}>
                      M-N-N:
                    </span>
                    <span>Morning -Noon- Night</span>
                  </div>

                  <div className="flex items-center border border-solid border-black p-1 rounded-lg">
                    <span>Instruction</span>
                  </div>

                  <div className="border border-solid border-black p-1 rounded-lg">
                    <span style={{ color: "black", fontWeight: "700" }}>
                      Note:
                    </span>
                    <span>
                      Medical substitution allowed wherever applicable
                    </span>
                  </div>
                </div>

                <div className="border-t-2 border-b-2 border-dashed border-gray-400 mt-4"></div>

                {/* Advice and Instructions */}
                {formData.specialInstructions && (
                  <>
                    <div className="mt-3" style={{ color: "#29a359" }}>
                      Advice and Instructions
                    </div>
                    <div className="flex justify-between mt-5 mb-7">
                      <ul className="list-none space-y-2">
                        {formatToArray(formData.specialInstructions).length >
                        0 ? (
                          formatToArray(formData.specialInstructions).map(
                            (instruction, index) => (
                              <li key={index} className="flex items-start">
                                <span
                                  style={{
                                    color: "#29a359",
                                    marginRight: "8px",
                                  }}
                                >
                                  •
                                </span>
                                <span>{instruction}</span>
                              </li>
                            )
                          )
                        ) : (
                          <li className="flex items-start">
                            <span
                              style={{ color: "#29a359", marginRight: "8px" }}
                            >
                              •
                            </span>
                            <span>N/A</span>
                          </li>
                        )}
                      </ul>
                      {formData.doctorDetails?.signatureImage && (
                        <div>
                          <div>
                            <img
                              src={formData.doctorDetails.signatureImage}
                              alt="doctor-signature"
                              width={"60px"}
                            />
                          </div>
                          <div>{formData.doctorDetails.name}</div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PrescriptionCard;

export const PrescriptionCardWithRef = React.forwardRef<
  PrescriptionCardHandle,
  PrescriptionCardProps
>((props, ref) => {
  const componentRef = useRef<PrescriptionCardHandle>(null);

  useImperativeHandle(ref, () => ({
    capturePrescriptionImage: () =>
      componentRef.current?.capturePrescriptionImage() ?? Promise.resolve(null),
  }));

  return <PrescriptionCard ref={componentRef} {...props} />;
});
